/**
 * Access governance — Zod schemas for the unified queue
 * (recovery + access requests in one surface).
 */

import { z } from "zod";
export { groupAccessRequestCreateSchema } from "./request.generated";
export type GroupAccessRequestInput = z.infer<typeof import("./request.generated").groupAccessRequestCreateSchema>;

export const requestKindSchema = z.enum(["recovery", "access"]);
export type RequestKind = z.infer<typeof requestKindSchema>;

export const decisionKindSchema = z.enum([
  "approved",
  "denied",
  "needs_info",
  "reversed",
]);
export type DecisionKind = z.infer<typeof decisionKindSchema>;

export const attestationMethodSchema = z.enum([
  "video_call",
  "in_person",
  "phone_call",
  "government_id",
  "manager_attestation",
  "security_question",
  "other",
]);
export type AttestationMethod = z.infer<typeof attestationMethodSchema>;

export { queueEntrySchema, queueResponseSchema } from "@/lib/governance-contract";
export type QueueEntry = z.infer<typeof import("@/lib/governance-contract").queueEntrySchema>;
export type QueueResponse = z.infer<typeof import("@/lib/governance-contract").queueResponseSchema>;

// A request's discussion, decisions, attestations and processing health are
// contract-generated: the discussion carries chat, needs_info and lifecycle
// messages (approve and deny verdicts are in the decisions).
export {
  attestationSchema as attestationRowSchema,
  decisionRecordSchema as decisionRowSchema,
  processingHealthSchema as jitHealthSchema,
  threadCommentSchema,
} from "@/lib/governance-contract";
export type ThreadComment = z.infer<typeof import("@/lib/governance-contract").threadCommentSchema>;
export type CommentSource = ThreadComment["Source"];
export type DecisionRow = z.infer<typeof import("@/lib/governance-contract").decisionRecordSchema>;
export type AttestationRow = z.infer<typeof import("@/lib/governance-contract").attestationSchema>;
export type JITHealth = z.infer<typeof import("@/lib/governance-contract").processingHealthSchema>;

export const queueFilterSchema = z.object({
  kind: requestKindSchema.optional(),
  priority: z.enum(["critical", "high", "normal"]).optional(),
  // "all" means show every status. Anything else (or unset) shows only
  // open requests (pending + awaiting_info) — the backend's default.
  status: z.enum(["all"]).optional(),
  // SLA triage filter. The UI exposes two: "breaching" (at/over SLA — overdue OR
  // escalates within 24h) and "overdue" (the urgent subset past its escalation
  // point, which the access escalation worker has/will widen to the admin pool).
  // The "show me what's at/over SLA" control that turns the queue into governance.
  // "due_today" is also accepted (the backend's third value, and any stale link):
  // the toolbar folds it into "breaching". `.catch(undefined)` so an unrecognized
  // sla degrades to the unfiltered queue instead of throwing the route boundary.
  sla: z.enum(["breaching", "overdue", "due_today"]).optional().catch(undefined),
  page: z.coerce.number().int().min(1).optional(),
  // Currently-focused request id (drives the master-detail right pane).
  selected: z.string().uuid().optional(),
});
export type QueueFilter = z.infer<typeof queueFilterSchema>;

// Terminal (decided/closed) statuses — the History view's universe. A request
// leaves the live queue into exactly one of these.
export const decidedStatusSchema = z.enum(["approved", "denied", "expired", "cancelled"]);
export type DecidedStatus = z.infer<typeof decidedStatusSchema>;

// historyFilterSchema drives the read-only Identity Governance History view
// (decided/closed requests, date-range + export). URL-driven like every other
// list/filter state. `from`/`to` are yyyy-mm-dd (the native date input value);
// the query layer expands them to an inclusive RFC3339 day range for the backend.
export const historyFilterSchema = z.object({
  kind: requestKindSchema.optional(),
  // Unset = all decided statuses; set = pin one terminal status.
  status: decidedStatusSchema.optional(),
  from: z.string().optional(),
  to: z.string().optional(),
  page: z.coerce.number().int().min(1).optional(),
});
export type HistoryFilter = z.infer<typeof historyFilterSchema>;
